import React, { FC, useEffect, useState } from "react";
import { FormGroup, Label, Input, FormFeedback } from "reactstrap";

interface Option {
  value: string;
  label: string;
}

interface DropdownWithValueNameProps {
  label: string;
  name: string;
  value: string;
  setValue: (name: string, value: string) => void;
  setError?: (name: string, error: boolean) => void;
  options: Option[]; 
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  design?: "flex" | "default"; 
  passCol?: string;
  tabIndex?: number;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => void;
  placeholder?: string;
   hideDefaultOption?: boolean;
}

const DropdownWithValueName: FC<DropdownWithValueNameProps> = ({
  label,
  name,
  value,
  setValue,
  setError,
  options,
  error,
  errorMessage,
  required = false,
  className,
  disabled,
  design = "default", 
  passCol,
  tabIndex,
  onBlur,
  placeholder,
   hideDefaultOption = false,
}) => {
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValue(name, value);
    if (setError) {
      setError(name, false);
    }
  };

  const flexClass = design === "flex" && windowWidth > 768 ? "d-flex align-items-center" : "";

  const labelWidthClass = design === "flex" && windowWidth > 768 ? "col-3" : ""; 
  const passColClass = windowWidth > 768 ? passCol : "";

  // Find if there's an option with matching value or label
  const findMatchingOption = () => {
    if (value === undefined || value === null || value === "") return "";
    
    const valueStr = value.toString();
    // First try exact match on value
    const exactMatch = options.find(option => option.value === valueStr);
    if (exactMatch) return valueStr;
    
    // Then try match on label
    const labelMatch = options.find(option => option.label === valueStr || option.label === value);
    if (labelMatch) return labelMatch.value;
    
    // If no match found, return the original value
    return valueStr;
  };

  return (
    <FormGroup className={flexClass}>
      {label && (
        <Label for={name} className={`${design === "flex" ? "mr-5 " : ""}${labelWidthClass}`}>
          {label}
          {required && <span className="text-danger ml-2">*</span>}
        </Label>
      )}
      <div className={passColClass}>
        {error && <FormFeedback className="d-block mt-1">{errorMessage}</FormFeedback>}
        <Input
          type="select"
          name={name}
          id={name}
          value={findMatchingOption()}
          tabIndex={tabIndex} 
          onChange={handleChange}
          onBlur={onBlur}
          invalid={error}
          disabled={disabled}
        >
          {!hideDefaultOption && (
            <option value="">{placeholder || "All"}</option>
          )}
          {options.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      </div>
    </FormGroup>
  );
};

export default DropdownWithValueName;